import Toast from 'react-native-toast-message';
import { supabase } from './client';
import ErrorHandler from '@/utils/ErrorHandler';
import { isTransientError } from '@/utils/requestQueue';
import { milestoneHistoryToSeries } from '@/utils/milestones';

/**
 * Thin authenticated adapters for the Milestones RPCs defined in
 * MILESTONES-IMPLEMENTATION-PLAN.md §7.2. No aggregate computation happens
 * here — the server engine owns counts; clients display bounded results.
 *
 * Large counts may be serialized as decimal strings (bigint-safe); every
 * payload is normalized through `normalizeMilestonePayload`.
 */

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** User-facing copy for the stable MILESTONE_* error contract (§7.3). */
const MILESTONE_ERROR_MESSAGES = {
    MILESTONE_PERMISSION_DENIED: "You don't have permission to do that. Contact your Reach administrator if you think you should.",
    MILESTONE_NOT_FOUND: "That milestone could not be found. It may have been removed.",
    MILESTONE_INVALID_METRIC: "That trigger is not supported. Pick a metric from the list and try again.",
    MILESTONE_INVALID_FILTER: "One of the selected filters is no longer valid. Review the scope selections and try again.",
    MILESTONE_INVALID_THRESHOLDS: "Thresholds must be unique whole numbers in the supported range. Fix the highlighted tiers and try again.",
    MILESTONE_EARNED_TIER_LOCKED: "A tier that has already been earned can't be changed or removed. Refresh to see the latest tiers.",
    MILESTONE_TRIGGER_LOCKED: "The trigger and scope can't change after badges have been earned. Duplicate the milestone to create a new series instead.",
    MILESTONE_REVISION_CONFLICT: "Someone else changed this milestone while you were editing. Refresh to load the latest version, then try again.",
    MILESTONE_ARCHIVED: "That milestone has been archived. Earned badges stay visible, but the series can't be edited.",
    MILESTONE_LEASE_INVALID: "That celebration is no longer available. It may have already been shown on another device.",
};

/** Error carrying the stable contract code for UI-level handling. */
export class MilestoneError extends Error {
    constructor(code, fallbackMessage, original) {
        super(MILESTONE_ERROR_MESSAGES[code] ?? fallbackMessage ?? original?.message ?? 'Something went wrong');
        this.name = 'MilestoneError';
        this.milestoneCode = code ?? null;
        this.originalError = original ?? null;
        // Business/expected errors — callers should not treat these as bugs.
        this.expected = !!code;
    }
}

/**
 * Extracts the MILESTONE_* code from a PostgREST/RPC error. The backend
 * raises `MILESTONE_*` as the SQLSTATE or as a message prefix.
 * @param {object} error - supabase rpc error.
 * @returns {string|null}
 */
export const extractMilestoneCode = (error) => {
    if (!error) return null;
    if (typeof error.code === 'string' && error.code.startsWith('MILESTONE_')) return error.code;
    const match = `${error.message ?? ''} ${error.details ?? ''}`.match(/MILESTONE_[A-Z_]+/);
    return match?.[0] ?? null;
};

/**
 * Converts bigint-safe decimal strings back to numbers anywhere in an RPC
 * payload. Only pure integer strings within the safe range are converted;
 * UUIDs/dates/other text pass through untouched.
 * @param {*} value
 * @returns {*}
 */
export const normalizeMilestonePayload = (value) => {
    if (Array.isArray(value)) return value.map(normalizeMilestonePayload);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, v]) => [key, normalizeMilestonePayload(v)]));
    }
    if (typeof value === 'string' && /^-?\d+$/.test(value)) {
        const numeric = Number(value);
        // Leading-zero strings (e.g. "007") are identifiers, not numbers.
        if (Number.isSafeInteger(numeric) && String(numeric) === value) return numeric;
    }
    return value;
};

/**
 * Shared error path for all milestone RPCs. Stable MILESTONE_* codes become
 * expected MilestoneErrors with friendly copy (optionally toasted); anything
 * else goes through the standard ErrorHandler/Sentry path and is rethrown so
 * react-query/mutations still observe failure.
 * @param {object} error - supabase rpc error.
 * @param {object} meta - { operation, params, toast, friendly }.
 * @returns {MilestoneError|Error}
 */
const handleMilestoneRpcError = (error, { operation, params = {}, toast = false, friendly } = {}) => {
    const code = extractMilestoneCode(error);
    if (code) {
        const milestoneError = new MilestoneError(code, friendly, error);
        if (toast) Toast.show({ type: 'error', text2: milestoneError.message });
        return milestoneError;
    }

    const wrapped = Error(error.message || 'Milestone request failed');
    // Keep the transport details reachable — the celebration provider
    // treats a missing function (PGRST202/404, backend not deployed yet) as
    // a quiet "unavailable" instead of polling an ErrorHandler every cycle.
    wrapped.code = error.code;
    wrapped.status = error.status;
    wrapped.originalError = error;
    if (isTransientError(error)) {
        // Network/blip errors: let react-query's retry path handle them —
        // rethrowing without toasting keeps this consistent with
        // ErrorHandler's own transient behavior.
        return wrapped;
    }
    ErrorHandler(wrapped, { query: operation, ...params, error }, friendly ?? 'Something went wrong with milestones');
    return wrapped;
};

const callMilestoneRpc = async (name, params, options = {}) => {
    const { data, error } = await supabase.rpc(name, params);
    if (error) throw handleMilestoneRpcError(error, { operation: name, params: options.scrub === false ? {} : params, toast: options.toast, friendly: options.friendly });
    return normalizeMilestonePayload(data);
};

const isUuid = (id) => typeof id === 'string' && UUID_PATTERN.test(id);

/**
 * get_milestone_catalog() → metric descriptors, allowlists, bounds,
 * placeholders, builtins, evaluation flag, and tracking-start metadata.
 */
export const getMilestoneCatalog = async () => {
    const data = await callMilestoneRpc('get_milestone_catalog', {});
    return data ?? null;
};

/**
 * list_milestone_definitions(p_scope, p_limit, p_after) — one bounded page.
 * @param {object} params - { scope, limit, after }.
 * @returns {Promise<{definitions: array, nextAfter: string|null}>}
 */
export const listMilestoneDefinitions = async ({ scope, limit = 100, after = null } = {}) => {
    const data = await callMilestoneRpc('list_milestone_definitions', {
        p_scope: scope ?? null,
        p_limit: limit,
        p_after: after,
    });
    return { definitions: data?.definitions ?? [], nextAfter: data?.nextAfter ?? null };
};

/** get_milestone_definition(p_milestone_id). */
export const getMilestoneDefinition = async (milestoneID) => {
    if (!isUuid(milestoneID)) return null;
    const data = await callMilestoneRpc('get_milestone_definition', { p_milestone_id: milestoneID });
    return data ?? null;
};

/**
 * save_milestone(p_definition, p_expected_revision) — atomic create/update.
 * @param {object} params - { definition, expectedRevision } (null revision for create).
 */
export const saveMilestone = async ({ definition, expectedRevision = null } = {}) => {
    const data = await callMilestoneRpc('save_milestone', {
        p_definition: definition,
        p_expected_revision: expectedRevision,
    }, { toast: true });
    return data ?? null;
};

/** set_milestone_enabled(p_milestone_id, p_enabled, p_expected_revision). */
export const setMilestoneEnabled = async ({ milestoneID, enabled, expectedRevision } = {}) => {
    if (!isUuid(milestoneID)) throw new MilestoneError('MILESTONE_NOT_FOUND');
    const data = await callMilestoneRpc('set_milestone_enabled', {
        p_milestone_id: milestoneID,
        p_enabled: enabled === true,
        p_expected_revision: expectedRevision,
    }, { toast: true });
    return data ?? null;
};

/** archive_milestone(p_milestone_id, p_expected_revision) — badges/history remain. */
export const archiveMilestone = async ({ milestoneID, expectedRevision } = {}) => {
    if (!isUuid(milestoneID)) throw new MilestoneError('MILESTONE_NOT_FOUND');
    const data = await callMilestoneRpc('archive_milestone', {
        p_milestone_id: milestoneID,
        p_expected_revision: expectedRevision,
    }, { toast: true });
    return data ?? null;
};

/**
 * get_person_milestone_state(p_person_id, p_limit, p_cursor) — grouped
 * visible badges/progress/counts. The server hides financial awards the
 * viewer cannot see BEFORE computing counts — do not re-filter here.
 * @returns {Promise<import('../types/Milestone').PersonMilestoneState>}
 */
export const getPersonMilestoneState = async ({ personID, limit = 50, cursor = null } = {}) => {
    if (!isUuid(personID)) return { series: [], earnedThresholdCount: 0, nextCursor: null };
    const data = await callMilestoneRpc('get_person_milestone_state', {
        p_person_id: personID,
        p_limit: limit,
        p_cursor: cursor,
    });
    return {
        series: data?.series ?? data?.groups ?? [],
        earnedThresholdCount: data?.earnedThresholdCount ?? 0,
        trackingStartedAt: data?.trackingStartedAt ?? null,
        nextCursor: data?.nextCursor ?? null,
    };
};

/**
 * get_organization_milestone_history(p_limit, p_cursor) — org awards +
 * progress, with the financial gate applied server-side.
 * @returns {Promise<import('../types/Milestone').OrganizationMilestoneHistory>}
 */
export const getOrganizationMilestoneHistory = async ({ limit = 50, cursor = null } = {}) => {
    const data = await callMilestoneRpc('get_organization_milestone_history', {
        p_limit: limit,
        p_cursor: cursor,
    });
    return { series: data?.series ?? milestoneHistoryToSeries(data), nextCursor: data?.nextCursor ?? null };
};

/**
 * get_milestone_award_replay(p_award_id) — authorized stored snapshot for a
 * manual badge replay in the shared achievement toast. No award mutation.
 * @returns {Promise<import('../types/Milestone').MilestoneAwardReplay|null>}
 */
export const getMilestoneAwardReplay = async (awardID) => {
    if (!isUuid(awardID)) return null;
    const data = await callMilestoneRpc('get_milestone_award_replay', { p_award_id: awardID }, { toast: true });
    if (!data) return null;
    const snapshot = data.snapshot ?? {};
    return {
        ...data,
        id: data.awardID,
        milestoneName: snapshot.name,
        title: snapshot.title,
        message: snapshot.message,
        badgeIcon: snapshot.badgeIcon,
        badgeColorToken: snapshot.badgeColorToken,
        earnedAt: data.recordedAt,
    };
};

/* -------------------------------------------------------------------------
 * Phase E presentation RPCs — implemented now so the data layer is complete;
 * the provider/modal that consumes them ships in the follow-up task.
 * ---------------------------------------------------------------------- */

/** sync_own_milestone_eligibility() — bounded receipt catch-up for own membership/audiences. */
export const syncOwnMilestoneEligibility = async () => {
    const data = await callMilestoneRpc('sync_own_milestone_eligibility', {});
    return data ?? { synced: 0, more: false };
};

/** get_own_milestone_celebration_state() — preference + compact pending metadata. */
export const getOwnMilestoneCelebrationState = async () => {
    const data = await callMilestoneRpc('get_own_milestone_celebration_state', {});
    return data ?? null;
};

/**
 * claim_own_milestone_celebrations(p_limit) — atomic lease for one grouped
 * batch (highest eligible pending tier per series). Empty when none/opted out.
 * @returns {Promise<import('../types/Milestone').MilestoneCelebrationClaim|null>}
 */
export const claimOwnMilestoneCelebrations = async ({ limit = 20 } = {}) => {
    const data = await callMilestoneRpc('claim_own_milestone_celebrations', { p_limit: limit });
    if (!data) return null;
    return {
        ...data,
        items: (data.items ?? []).map(item => ({
            ...item,
            milestoneName: item.milestoneName ?? item.series ?? item.name ?? 'Milestone',
        })),
    };
};

/**
 * handle_own_milestone_celebrations(p_lease_id, p_action) — idempotent
 * VIEWED/RELEASE handling for the exact owned lease.
 * @param {'VIEWED'|'RELEASE'} action
 */
export const handleOwnMilestoneCelebrations = async ({ leaseID, action } = {}) => {
    if (!isUuid(leaseID) || !['VIEWED', 'RELEASE'].includes(action)) {
        throw new MilestoneError('MILESTONE_LEASE_INVALID');
    }
    const data = await callMilestoneRpc('handle_own_milestone_celebrations', {
        p_lease_id: leaseID,
        p_action: action,
    });
    return data ?? null;
};

/**
 * set_own_milestone_celebrations_enabled(p_enabled) — account-level
 * automatic-celebrations preference. Suppresses pending across the account's
 * orgs atomically; re-enabling does not unsuppress receipts.
 */
export const setOwnMilestoneCelebrationsEnabled = async (enabled) => {
    const data = await callMilestoneRpc('set_own_milestone_celebrations_enabled', { p_enabled: enabled === true }, { toast: true });
    return data ?? null;
};

/**
 * record_own_reach_active_day() — own verified server-local day write.
 * @returns {Promise<{recorded: boolean, localDate: string, trackingStartedAt: string|null}>}
 */
export const recordOwnReachActiveDay = async () => {
    const data = await callMilestoneRpc('record_own_reach_active_day', {});
    return data ?? { recorded: false };
};
